import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/all";

import styles from "./moonshot.module.scss";

const Moonshot = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const smallDevice = windowWidth <= 990;
  const largeDevice = windowWidth > 990;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    setWindowWidth(window.innerWidth);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const mainTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: "#moonshotWrapper",
        scrub: true,
        end: "+=750",
      },
    });

    const moonTimeline = gsap.timeline();
    moonTimeline.from("#moon", {
      y: 350,
      duration: 90,
      opacity: 0.8,
      delay: 4,
    });

    const textTimeline = gsap.timeline();
    textTimeline.from("#textContent", {
      y: 500,
      duration: 95,
      opacity: 0.5,
      delay: 5,
    });

    mainTimeline.add(moonTimeline, 12);
    mainTimeline.add(textTimeline, 24);
  }, []);

  return (
    <section className={styles.moonshotWrapper} id="moonshotWrapper">
      <div className={styles.sceneSection}>
        <div className={styles.moonWrapper}>
          <img
            className={styles.moon}
            id="moon"
            src={"/images/moonshot/moon.png"}
            alt=""
          />
          <div className={styles.textContent} id="textContent">
            <p className={styles.theMoonshot}>
              <span>The</span>
              <span>Moonshot!</span>
            </p>
            <p className={styles.staffFavorite}>Our Staff Favorite</p>
            <a href="https://campaigns-linkurious.cleverstory.io/banking-resources-linkurious" target="_blank">
              <img
                className={styles.moonshotLogo}
                src={"/images/moonshot/linkuriousLogo.png"}
              />
            </a>
          </div>
        </div>
        <img
          className={styles.terrainImg}
          src={
            largeDevice
              ? "/images/moonshot/terrain_desktop.png"
              : "/images/moonshot/terrain_mobile.png"
          }
        />
        <img
          className={styles.astronaut}
          src={"/images/moonshot/astronaut.png"}
          alt=""
        />
      </div>

      <div className={styles.contentSection}>
        {largeDevice &&
          <div className={styles.imageSection}>
            <a href="https://campaigns-linkurious.cleverstory.io/banking-resources-linkurious" target="_blank">
              <img
                className={styles.websiteImg} src="/images/moonshot/linkurious.png"
              />
            </a>
            <img
              className={styles.builtIn2days}
              src={"/images/moonshot/builtIn2days.png"}
              alt=""
            />
            <img
              className={styles.builtWithCleverstory}
              src={"/images/moonshot/builtWithCleverstory.png"}
              alt=""
            />
          </div>
        }
        <div className={styles.textSection}>
          <p className={styles.timeline}>MARCH, 2026</p>
          <h2 className={styles.heading}>
            How Linkurious Turned Product Breadth Relevant to Each Buyer
          </h2>
          <div className={styles.QnA}>
            {smallDevice &&
              <div className={styles.imageSection}>
                <img
                  className={styles.websiteImg} src="/images/moonshot/linkurious.png"
                />

                <img
                  className={styles.builtIn2days}
                  src={"/images/moonshot/builtIn2days.png"}
                  alt=""
                />
                <img
                  className={styles.builtWithCleverstory}
                  src={"/images/moonshot/builtWithCleverstory.png"}
                  alt=""
                />
              </div>
            }
            <div className={styles.answer}>
              <span className={styles.paragraph}>
                When a product can solve for multiple industries and use cases, the default instinct is to lead with breadth.
              </span>

              <span className={styles.paragraph}>
                Show the whole platform. <br />
                List the use cases. <br />
                Talk about flexibility. <br />
                Let the buyer figure out where they fit.
              </span>

              <span className={styles.paragraph}>
                But that creates a quieter problem.
              </span>

              <span className={styles.paragraph}>
                The more possibilities you show upfront, the more interpretation work you hand back to the reader.
              </span>

              <span className={styles.paragraph}>
                And for buyers in high-stakes categories like banking and financial crime, that is friction.
              </span>

              <span className={styles.paragraph}>
                They are not showing up to admire the platform breadth. <br /> They are showing up with a sharper question:
              </span>

              <span className={styles.paragraph}>
                Can this help us investigate risk faster, connect scattered signals, and make better decisions in the moments that matter?
              </span>

              <span className={styles.paragraph}>
                That is what makes this Linkurious experience different.
              </span>

              <span className={styles.paragraph}>
                Linkurious works across industries and use cases, but this page does not try to make every story visible at once. Instead, it chooses a clear entry point: financial crime in banking.
              </span>

              <span className={styles.paragraph}>
                That choice matters.
              </span>

              <span className={styles.paragraph}>
                Because once the story narrows, the value gets easier to feel. <br /> Instead of asking the buyer to decode a broad platform on their own, the experience meets them inside a familiar problem space: AML pressure, complex investigations, fragmented information, and the need to connect the dots faster.
              </span>

              <span className={styles.paragraph}>
                That is what helps turn a broad product story into something a buyer can actually follow.
              </span>

              <span className={styles.paragraph}>
                It also shows the kind of resource-hub experience that becomes possible when content is built to be more tailored, more guided, and easier to learn from over time.
              </span>

              <span className={styles.paragraph}>
                When a product can speak to many audiences, a resource hub’s job is not to say everything at once.
              </span>

              <span className={styles.paragraph}>
                It is to make the right story easier to find, follow, and act on.
              </span>

              <span className={styles.paragraph}>
                This version is stronger because it keeps the strategy, but trims the product-padding that was slowing it down.
              </span>
            </div>
            <div className={styles.liveExperienceLink}>
              <a href="https://campaigns-linkurious.cleverstory.io/banking-resources-linkurious" target="_blank">
                Live Experience
              </a>
            </div>
            {smallDevice &&
              <img
                className={styles.astronautImage}
                src={"/images/astronaut.png"}
                alt=""
              />
            }
          </div>
        </div>
      </div>
    </section>
  );
};

export default Moonshot;
